import { WebPortalClient, StudyScheduleSession, FvcAnalysisSet, SubjectCreation, DiaryPackageDeployment, DiarySession } from "webportalclient";
import * as Moment from "moment";
import { OrderDirection, Package, VitalographUpdateClient } from "@vitalograph/update-client";
import { compare as semVerCompare } from "semver";
import * as P230In2itiveApi from '../../../misc/api/DeviceLinkApi/P230In2itive';
import * as P230Messages from "../../../misc/api/DeviceLinkApi/P230In2itiveMessages";
import { SpirometryAnalysisSetTypeEnum } from "../../../misc/api/ApiSpirometryAnalysisSetType";
import { DeviceTypes, OverreadStatusIdEnum, Subject } from "../../../misc/api/apiTypes";
import { Device } from "../../../misc/api/DeviceLinkApi/Devices";
import { UploadStatus } from "../../../Devices/DevicesNewReducer";
import { UploadDiarySessionsAsyncAction } from "../../../Devices/UploadDiarySessionsAsyncAction";
import { Study } from "webportalclient";
import { ConfigureDiaryDeployments } from "../P230In2itiveEDiary/ConfigurationHelpers";
import { WebPortalLegacyClientInternal } from "../../../misc/api/WebPortalTokenClient";
import { areSame } from "../../../misc/ArrayFunctions";
import { NIL } from 'uuid';

const applyLatestFirmware = async (device: Device, subject: Subject, updateServiceUrl: string, onError: (message: string) => void, setStatus: (status: string) => void,) => {
    const updateClient = new VitalographUpdateClient(updateServiceUrl);
    let firmwareResults = [];

    const protocolValue = subject.Study.ProtocolNumber;

    const studyProtocolString = protocolValue.replace(/[!@#$%^&*()_+={}\[\]:;"'<>,.?/|\\`~\-]/g, '');

    try {
        firmwareResults = await (await updateClient.package_PostPackages({
            filters: [
                {
                    member: "Name",
                    values: [`In2itiveEDiary${studyProtocolString}`, "In2itiveEDiary"]
                }
            ],
            order: {
                member: "Version",
                direction: OrderDirection.Descending
            },
        })).data;
    }
    catch (ex) {
        return;
    }

    const studySpecificFirmware = firmwareResults.filter(f => f.name.includes(studyProtocolString));

    const genericFirmware = [];
    for (const firmware of firmwareResults) {
        if (!firmware.name.includes(studyProtocolString))
            genericFirmware.push(firmware);
    }

    let latestFirmware: Package = null;
    if (studySpecificFirmware.length === 0)
        latestFirmware = genericFirmware.length > 0 ? genericFirmware[0] : null;
    else
        latestFirmware = studySpecificFirmware[0];

    if (latestFirmware === null)
        return;

    // HACK: Ensure the firmware version number meets semantic versioning guidelines
    const currentFirmwareSemVer = device.firmware.split(".").map(v => parseInt(v)).join(".");
    const diff = semVerCompare(latestFirmware.version, currentFirmwareSemVer);

    if (diff !== 1)
        return;

    setStatus(P230Messages.UpdateDeviceFirmware);

    let packageResult: Package;
    try {
        packageResult = await updateClient.package_GetPackage(latestFirmware.id);
    }
    catch (ex) {
        return;
    }

    try {
        if (await P230In2itiveApi.ApplicationUpdate(device.id, packageResult.contentUrl)) {
            await P230In2itiveApi.RestartWithWait(device.id);
        }
    }
    catch {
        return onError(P230Messages.FailedToUpdateDeviceFirmware);
    }
}

const uploadExistingSubjectData = async (client: WebPortalClient, study: Study, selectedDevice: Device, currentDeviceSubject: SubjectCreation, packageDeployments: DiaryPackageDeployment[], setStatus: React.Dispatch<React.SetStateAction<string>>, onError: (message: string) => void) => {
    let allSessions = [];
    
    const getSessions = async (lastRecordId?: string): Promise<UploadStatus> => {
        const result = await P230In2itiveApi.GetSession(selectedDevice.id);

        if (result === undefined)
            return UploadStatus.Uploaded;

        if (P230In2itiveApi.isIn2itiveError(result)) {
           
            return UploadStatus.PartialFailed;
        }

        allSessions.push(...result.eDiarySessions);

        if (result.eDiarySessions.length === 100) {
            const lastId = result.eDiarySessions[result.eDiarySessions.length - 1].id;
            return getSessions(lastId);
        }
        return;
    }

    const status = await getSessions(selectedDevice.id);
    if (status !== undefined)
        return { status: status };

    // HACK: For eDiary version 1.05.0040
    allSessions = allSessions.filter(x => x.id !== NIL);
    // ENDHACK

    const diarySessions: DiarySession[] = allSessions.map(s => {
        return {
            id: s.id,
            visitId: s.visitId,
            name: s.name,
            occurred: s.occurred,
            batteryLevel: s.batteryLevel,
            applicationVersion: s.applicationVersion,
            serialNumber: s.serialNumber,
            pefRepeatable: s.pefRepeatable,
            pefRepeatability: s.pefRepeatability,
            tests: s.tests ? s.tests.map(t => {
                return {
                    id: undefined,
                    sessionId: s.id,
                    occurred: t.occurred,
                    index: t.index,
                    accepted: t.accepted,
                    qualityIndicators: t.qualityIndicators,
                    pef: t.pef,
                    fev1: t.fev1
                }
            }) : undefined,
            questionnaires: s.questionnaires ? s.questionnaires.map(q => {
                return {
                    id: q.id,
                    sessionId: s.id,
                    questionnaireId: q.questionnaireId,
                    score: q.score,
                    occurred: q.occurred,
                    name: q.name,
                    answers: q.answers ? q.answers.map(a => {
                        return {
                            id: undefined,
                            questionnaireTestId: q.id,
                            questionIndex: a.questionIndex.toString(),
                            answerIndex: a.answerIndex.toString(),
                            answerWeight: a.answerWeight,
                            answer: a.answer,
                        }
                    }) : undefined
                }
            }) : undefined
        } as DiarySession;
    });
    let existingSessions = [];
    for (const session of diarySessions) {
        
        const getSessionById = await client.getSessions(undefined, `id eq ${session.id}`);
        if (getSessionById["@odata.count"] > 0) {
            existingSessions.push(getSessionById.value);
        }
    }

    if (existingSessions.length >= diarySessions.length)
        return UploadStatus.Uploaded;
    setStatus(P230Messages.UploadExistingData);
    return await UploadDiarySessionsAsyncAction.UploadSingle(client, study.id, selectedDevice, diarySessions, currentDeviceSubject, () => { }, (status, message) => status !== UploadStatus.Uploaded ? onError(message) : {});
}

const allocateDevice = async (client: WebPortalClient, serial: string, subjectId: string, timeWindowStartTime: string, onError: (message: string) => void) => {

    const startTimeCurrentDay = Moment(timeWindowStartTime, "HH:mm:ss").format();

    const getDeviceId = await client.getDevices(undefined, `SerialNumber eq '${serial}'`);

    if (getDeviceId.value.length === 0)
        return onError(P230Messages.NoDeviceInWebPortal);

    await client.patchDevice({
        allocatedSubjectId: subjectId
    },
        getDeviceId.value[0].id);

    await client.patchSubjects({
        diaryComplianceDateTime: startTimeCurrentDay
    },
        subjectId);
}

export const ConfigureP230Device = async (
    client: WebPortalClient,
    legacyClient: WebPortalLegacyClientInternal,
    study: Study,
    subject: Subject,
    selectedDevice: Device,
    sessionScheduleItems: StudyScheduleSession[],
    updateServiceUrl: string,
    setStatus: (status: string) => void,
    onError: (message: string) => void) => {
    const trimmedSerial = selectedDevice.serial;
    let deviceId = selectedDevice.id;

    try {
        let allocations = await client.getDevices(undefined, `SerialNumber eq '${trimmedSerial}'`);

        const packageDeployments = await client.getDiaryPackageDeployments(undefined, `serialNumber eq '${trimmedSerial}'`, undefined, "sessionSchedule");

        selectedDevice = await P230In2itiveApi.ReConnectToDevice();
        deviceId = selectedDevice.id;

        const subjectResult = await P230In2itiveApi.GetSubject(deviceId);

        if (subjectResult) {
            const uploadResult = await uploadExistingSubjectData(client, study, selectedDevice, subjectResult.subject, packageDeployments.value, setStatus, onError);

            if (uploadResult === UploadStatus.Failed || uploadResult === UploadStatus.PartialFailed)
                return false;

            if (subjectResult.subject.id !== subject.Id) {
                setStatus(P230Messages.ConfiguringDeviceForNewSubject);
                const clearResult = await P230In2itiveApi.ClearMemory(deviceId);
                if (!clearResult) {
                    onError(P230Messages.FailedToClear);
                    return false;
                }

                await P230In2itiveApi.RestartWithWait(deviceId);

                selectedDevice = await P230In2itiveApi.ReConnectToDevice();

                allocations = await client.getDevices(undefined, `SerialNumber eq '${trimmedSerial}'`);
            }
        }

        await applyLatestFirmware(selectedDevice, subject, updateServiceUrl, onError, setStatus);

        selectedDevice = await P230In2itiveApi.ReConnectToDevice();

        if (allocations["@odata.count"] === 0) {
            onError(P230Messages.NoDeviceInWebPortal);
            return false;
        }

        if (allocations["@odata.count"] > 0 && allocations.value[0].allocatedSiteId !== null) {
            onError(P230Messages.IncorrectlyAllocatedToSite);
            return false;
        }

        // HACK: /api/v3 needs to support the Diary specific session schedule item
        const diarySessionScheduleItem = await legacyClient.getDiarySessionScheduleItemAsync(sessionScheduleItems[sessionScheduleItems.length - 1].id);

        if (allocations["@odata.count"] > 0 && allocations.value[0].allocatedSubjectId === null)
            await allocateDevice(client, trimmedSerial, subject.Id, diarySessionScheduleItem.StartOfWindow, onError);

        if (allocations["@odata.count"] > 0 && allocations.value[0].allocatedSubjectId !== subject.Id)
            await allocateDevice(client, trimmedSerial, subject.Id, diarySessionScheduleItem.StartOfWindow, onError);

        if (allocations["@odata.count"] === 0 || allocations.value[0].allocatedSubjectId === null)
            await allocateDevice(client, trimmedSerial, subject.Id, diarySessionScheduleItem.StartOfWindow, onError);

        if (!areSame(sessionScheduleItems.map(ssi => ssi.id), packageDeployments.value.map(pd => pd.sessionScheduleId)))
            await allocateDevice(client, trimmedSerial, subject.Id, diarySessionScheduleItem.StartOfWindow, onError);

        allocations = await client.getDevices(undefined, `SerialNumber eq '${trimmedSerial}'`);

        if (!subjectResult) {
            await client.patchSubjects({
                diaryComplianceDateTime: Moment(diarySessionScheduleItem.StartOfWindow, "HH:mm:ss").format()
            },
                allocations.value[0].allocatedSubjectId);
        }

        await ConfigureDiaryDeployments(client, subject.Id, sessionScheduleItems, trimmedSerial);

        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        const configurationResult = await client.deviceConfiguration({ serialNumber: selectedDevice.serial, deviceType: DeviceTypes.In2itiveP230EDiary, subjectId: subject.Id, timeZone: localTimeZone });

        const dateTimeResult = await P230In2itiveApi.ConfigureDateTime(selectedDevice.id, Moment().local().format());

        if (!dateTimeResult) {
            onError(P230Messages.FailedToConfigureDateTimeMessage);
            return false;
        }

        const updateResult = await P230In2itiveApi.ConfigurationUpdate(selectedDevice.id, configurationResult);
        if (!updateResult) {
            onError(updateResult.error.map(e => e.message).join("\n"));
            return false;
        }

        const restartResult = await P230In2itiveApi.Restart(selectedDevice.id);
        if (!restartResult) {
            onError(P230Messages.FailedToRestartDevice);
            return false;
        }

        return true;
    }
    catch (err) {
        err.message ? onError(err.message) : onError(P230Messages.FailedToConfigureMessage);
        return false;
    }
}

export const ConfigureP230HomeSpirometrySession = async (client: WebPortalClient, serialNumber: string, subject: Subject, sessionScheduleItem: StudyScheduleSession, createSession: boolean): Promise<FvcAnalysisSet> => {
    let visitsResult = (await client.getVisits("id", `subjectId eq ${subject.Id} and visitScheduleItemId eq ${sessionScheduleItem.studyScheduleVisitId}`)).value[0];

    if (visitsResult === null || visitsResult === undefined) {
        const visitScheduleItem = await client.getStudyScheduleVisit(sessionScheduleItem.studyScheduleVisitId, "id,label");

        visitsResult = await client.postVisit({
            name: visitScheduleItem.label,
            subjectId: subject.Id,
        });
    }

    if (createSession) {
        let fvcResult = (await client.getFvcSessions("id", `visitId eq ${visitsResult.id} and name eq '${sessionScheduleItem.label}' and serialNumber eq '${serialNumber}'`)).value[0];

        if (fvcResult === null || fvcResult === undefined) {
            fvcResult = await client.postFvcSession({
                name: sessionScheduleItem.label,
                occurred: new Date().toISOString(),
                serialNumber: serialNumber,
                status: "FVCS_INPROGRESS",
                visitId: visitsResult.id,
            });
        }

        let analysisSetResult = (await client.getSpirometryAnalysisSets("id", `sessionId eq ${fvcResult.id} and type eq '${SpirometryAnalysisSetTypeEnum.Site}'`)).value[0];

        if (analysisSetResult === null || analysisSetResult === undefined) {
            analysisSetResult = await client.postSpirometryAnalysisSet({
                sessionId: fvcResult.id,
                type: SpirometryAnalysisSetTypeEnum.Site,
                status: OverreadStatusIdEnum.NotStarted,
            });
        }

        return analysisSetResult;
    }

    return null;
}
